// eslint-disable-next-line no-restricted-imports -- TextField is needed for free-text "Other" input
import { Stack, TextField } from "@mui/material";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { MultiSelectQuestion } from "../components/MultiSelectQuestion";
import { SurveyStepLayout } from "../components/SurveyStepLayout";
import { EMR_OTHER_VALUE, type ScreeningSurveyFormData } from "../lib/screeningSurveySchema";
import { type EmrSystem, emrSystemLabels } from "../lib/screeningSurveyTypes";

const options = Object.entries(emrSystemLabels).map(([value, label]) => ({
  value: value as EmrSystem,
  label,
}));

interface EmrSystemsStepProps {
  onContinue: () => void;
  onBack: () => void;
  currentStep: number;
  totalSteps: number;
}

export function EmrSystemsStep(props: EmrSystemsStepProps) {
  const { onContinue, onBack, currentStep, totalSteps } = props;
  const { watch, setValue } = useFormContext<ScreeningSurveyFormData>();
  const value = watch("emrSystems") ?? [];
  const otherValue = watch("emrSystemsOther") ?? "";

  const isOtherSelected = value.includes(EMR_OTHER_VALUE);
  const isOtherInvalid = isOtherSelected && otherValue.trim().length === 0;
  const continueDisabled = value.length === 0 || isOtherInvalid;

  const otherInputRef = useRef<HTMLInputElement>(null);
  const wasOtherSelectedRef = useRef(isOtherSelected);
  useEffect(() => {
    if (!wasOtherSelectedRef.current && isOtherSelected) {
      otherInputRef.current?.focus();
      otherInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    wasOtherSelectedRef.current = isOtherSelected;
  }, [isOtherSelected]);

  return (
    <SurveyStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title="Which EMR systems have you used multiple times?"
      subtitle="Select all that apply."
      continueDisabled={continueDisabled}
      onBack={onBack}
      onContinue={onContinue}
    >
      <Stack spacing={4}>
        <MultiSelectQuestion
          options={options}
          selectedValues={value}
          onChange={(v) => {
            setValue("emrSystems", v);
            if (!v.includes(EMR_OTHER_VALUE)) {
              setValue("emrSystemsOther", "");
            }
          }}
        />
        {isOtherSelected && (
          <TextField
            fullWidth
            required
            inputRef={otherInputRef}
            placeholder="Which system(s)?"
            value={otherValue}
            onChange={(event) => {
              setValue("emrSystemsOther", event.target.value);
            }}
          />
        )}
      </Stack>
    </SurveyStepLayout>
  );
}
